import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import { useReturnNavigation } from '../../orders/order-details/hooks/use-return-navigation';
import Global from '../../store/global-selectors';
import { goToOrderDetails } from '../../store/orders/order-details/orders.details.actions';
import { useIsOrdersGridEnabled } from '../../feature-gates/use-is-orders-grid-enabled';
import { DisabledResponse } from '../../feature-gates/use-plan-feature-gate';

const messages = defineMessages({
  inactiveStore: {
    defaultMessage: 'Sorry, that order could not be found. Please check that the store is active.',
    id: 'tHzBU2',
    description: 'orderNumberLink.inactiveStore',
  },
  outdatedOrder: {
    defaultMessage:
      "This order has changed status. Click Reload to remove from view. To change this behavior, go to your Account's Order Display Settings.",
    id: 'YGTUOn',
    description: 'Hover label for order number link for non-interactive order that includes instructions on how to clear',
  },
});

const shouldLinkBeDisabled = ({
  userHasAdminPowers,
  userHasOrderManagementRole,
  salesOrderAssignedUserId,
  currentUserId,
  storeIsInactiveAndNotBypassed,
  isOrdersGridEnabled,
}) => {
  if (!isOrdersGridEnabled) {
    return true;
  }
  if (storeIsInactiveAndNotBypassed) {
    return true;
  }
  if (userHasAdminPowers) {
    return false;
  }
  if (!userHasOrderManagementRole) {
    return true;
  }
  if (salesOrderAssignedUserId && salesOrderAssignedUserId !== currentUserId) {
    return true;
  }
  return false;
};

const getTooltip = (storeIsInactiveAndNotBypassed, isOutdated, isOrdersGridEnabled) => {
  if (!isOrdersGridEnabled) {
    return <FormattedMessage {...DisabledResponse.disabledMessage} />;
  }
  if (storeIsInactiveAndNotBypassed) {
    return <FormattedMessage {...messages.inactiveStore} />;
  }
  if (isOutdated) {
    return <FormattedMessage {...messages.outdatedOrder} />;
  }
};

const OrderNumberLinkComponent = ({
  id,
  salesOrderId,
  selectGridWhenClicked = false,
  orderNumber,
  salesOrderAssignedUserId = '',
  storeGuid = '',
  shouldBypassReadOnlyStoreCheck = false,
  isOutdated = false,
  returnFulfillmentId,
  buttonClassName,
}) => {
  const dispatch = useDispatch();

  const salesOrder = useSelector((state) => Global.selectEntitiesSalesOrderById(state, { salesOrderId }));
  const effectiveStoreGuid = salesOrder?.store?.storeGuid ?? storeGuid;
  const store = useSelector((state) =>
    effectiveStoreGuid
      ? Global.selectSellerStoreData(state, {
          storeGuid: effectiveStoreGuid,
        })
      : null,
  );
  const userIsSysAdmin = useSelector(Global.selectUserisSysAdmin);
  const userHasAdminRole = useSelector(Global.selectUserhasAdministrationRole);
  const userHasAdminPowers = userIsSysAdmin || userHasAdminRole;
  const userHasOrderManagementRole = useSelector(Global.selectUserhasOrderManagementRole);
  const currentUserId = useSelector(Global.selectUserCurrentUserId);
  const storeIsInactiveAndNotBypassed = !shouldBypassReadOnlyStoreCheck && !store?.isActive;
  const isOrdersGridEnabled = useIsOrdersGridEnabled();
  const disabled = shouldLinkBeDisabled({
    userHasAdminPowers,
    userHasOrderManagementRole,
    salesOrderAssignedUserId,
    currentUserId,
    storeIsInactiveAndNotBypassed,
    isOrdersGridEnabled,
  });

  const tooltip = getTooltip(storeIsInactiveAndNotBypassed, isOutdated, isOrdersGridEnabled);

  const { navigateToReturn } = useReturnNavigation();

  const onClick = useCallback(
    (e) => {
      e.target.blur();

      if (returnFulfillmentId) {
        navigateToReturn(salesOrderId, returnFulfillmentId);
      } else {
        dispatch(
          goToOrderDetails({
            salesOrderId,
            selectOrderInGrid: selectGridWhenClicked,
            fulfillmentPlanId: id,
          }),
        );
      }
    },
    [dispatch, id, navigateToReturn, returnFulfillmentId, salesOrderId, selectGridWhenClicked],
  );

  const button = (
    <Button className={buttonClassName} disabled={disabled} onClick={onClick} theme='link'>
      {orderNumber}
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipContent>{tooltip}</TooltipContent>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

export const OrderNumberLink = React.memo(OrderNumberLinkComponent);

OrderNumberLink.propTypes = {
  id: PropTypes.string,
  orderNumber: PropTypes.node.isRequired,
  salesOrderId: PropTypes.string,
  selectGridWhenClicked: PropTypes.bool,
  storeGuid: PropTypes.string,
  salesOrderAssignedUserId: PropTypes.string,
  shouldBypassReadOnlyStoreCheck: PropTypes.bool,
  isOutdated: PropTypes.bool,
  returnFulfillmentId: PropTypes.string,
  buttonClassName: PropTypes.string,
};
